/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import React, { FunctionComponent } from 'react'
import { AccessibilityRole } from 'react-native'
import Animated from 'react-native-reanimated'
import styled, { useTheme } from 'styled-components/native'

import { useHandleHover } from 'libs/hooks/useHandleHover'
import { getComputedAccessibilityLabel } from 'shared/accessibility/helpers/getComputedAccessibilityLabel'
import { ColorsType } from 'theme/types'
import { Typo } from 'ui/theme'

import { getButtonColors } from './Button.colors'
import {
  getBorderWidth,
  getContentPadding,
  getIconElement,
  getInteractiveColors,
  getLabelStyle,
  getLabelText,
  getSpacingGap,
  getTypographyForSize,
} from './Button.layout'
import { ButtonLoadingIndicator } from './ButtonLoadingIndicator'
import { ButtonColorValue, ButtonNativeProps, ButtonVariant, ButtonWebProps } from './types'

type ButtonContainerProps = {
  content: React.ReactNode
  variant: ButtonVariant
  accessibilityLabel: string
  accessibilityHint?: string
  accessibilityRole: AccessibilityRole
  testID?: string
  fullWidth?: boolean
  isDisabled: boolean
  isLoading?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  backgroundColor?: ButtonColorValue
  borderColor?: ColorsType
  borderWidth?: number
  paddingVertical: number
  paddingHorizontal: number
}

type ButtonBaseProps = (ButtonNativeProps | ButtonWebProps) & {
  defaultAccessibilityRole: AccessibilityRole
  renderContainer: (props: ButtonContainerProps) => React.ReactElement
}

export const ButtonBase: FunctionComponent<ButtonBaseProps> = ({
  variant = 'primary',
  size = 'default',
  hovered,
  color = 'brand',
  transparent = false,
  icon,
  disabled,
  iconPosition = 'left',
  iconButton = false,
  wording,
  numberOfLines,
  ellipsizeMode,
  accessibilityLabel,
  accessibilityHint,
  accessibilityRole,
  testID,
  fullWidth,
  isLoading,
  iconAnimatedStyle,
  defaultAccessibilityRole,
  renderContainer,
}: ButtonBaseProps) => {
  const theme = useTheme()
  const hoverProps = useHandleHover()
  const isDisabled = disabled === true
  const isHover = hovered === undefined ? hoverProps.isHover : hovered
  const isHoverActive = isHover && !isDisabled

  const colors = getButtonColors({
    theme,
    variant,
    color,
    transparent,
    disabled: isDisabled,
  })

  const { backgroundColor, borderColor, textColor, iconColor } = getInteractiveColors(
    colors,
    isHoverActive
  )
  const borderWidth = getBorderWidth(borderColor)
  const labelText = getLabelText(iconButton, wording)
  const computedA11yLabel = getComputedAccessibilityLabel(
    iconButton ? accessibilityLabel : (accessibilityLabel ?? wording ?? '')
  )
  const gap = getSpacingGap(!!labelText, theme.designSystem.size.spacing.s)
  const isMultiline = typeof numberOfLines === 'number' && numberOfLines > 1
  const labelStyle = getLabelStyle(textColor, fullWidth, isMultiline, !!icon)
  const typographyKey = getTypographyForSize(size)
  const LabelTypo = Typo[typographyKey]

  const iconSize =
    size === 'small' ? theme.designSystem.size.icon.buttonS : theme.designSystem.size.icon.button
  const iconColor2 = theme.designSystem.color.icon.inverted
  const getPositionedIcon = (position: 'left' | 'right') =>
    iconPosition === position
      ? getIconElement({ icon, color: iconColor, color2: iconColor2, size: iconSize })
      : null

  const leftIcon = getPositionedIcon('left')
  const rightIcon = getPositionedIcon('right')

  const resolvedAccessibilityRole = accessibilityRole ?? defaultAccessibilityRole

  const { vertical: paddingVertical, horizontal: paddingHorizontal } = getContentPadding({
    variant,
    iconButton,
    theme,
  })
  const content = (
    <Content gap={gap} fullWidth={fullWidth} isMultiline={isMultiline} hasIcon={!!icon}>
      {isLoading ? (
        <ButtonLoadingIndicator color={iconColor} size={iconSize} testID="button-loading" />
      ) : (
        <React.Fragment>
          {leftIcon ? <IconWrapper style={iconAnimatedStyle}>{leftIcon}</IconWrapper> : null}
          {labelText ? (
            <LabelTypo
              style={labelStyle}
              numberOfLines={numberOfLines}
              ellipsizeMode={ellipsizeMode}>
              {labelText}
            </LabelTypo>
          ) : null}
          {rightIcon ? <IconWrapper style={iconAnimatedStyle}>{rightIcon}</IconWrapper> : null}
        </React.Fragment>
      )}
    </Content>
  )

  return renderContainer({
    content,
    variant,
    accessibilityLabel: computedA11yLabel,
    accessibilityHint,
    accessibilityRole: resolvedAccessibilityRole,
    testID,
    fullWidth,
    isDisabled,
    isLoading,
    onMouseEnter: hoverProps.onMouseEnter,
    onMouseLeave: hoverProps.onMouseLeave,
    backgroundColor,
    borderColor,
    borderWidth,
    paddingVertical,
    paddingHorizontal,
  })
}

const Content = styled.View<{
  gap: number
  fullWidth?: boolean
  isMultiline?: boolean
  hasIcon?: boolean
}>(({ gap, fullWidth, isMultiline, hasIcon }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  columnGap: gap,
  flexWrap: isMultiline && !hasIcon ? 'wrap' : undefined,
  width: fullWidth ? '100%' : undefined,
  justifyContent: fullWidth ? 'center' : undefined,
}))

const IconWrapper = styled(Animated.View)({
  flexShrink: 0,
})
